import type { EliteBenefitsByLevel, EliteLevelBenefit } from '../hooks/api';

const LEVEL_NAMES: Record<string, string> = {
	level_1: 'Azul',
	level_2: 'Plata',
	level_3: 'Oro',
	level_4: 'Platino',
	level_5: 'Black',
	level_6: 'Nivel 6'
};

const PRODUCT_NAMES: Record<string, string> = {
	phone: 'Android',
	iphone: 'iPhone',
	tablet: 'Tablet',
	laptop: 'Laptop'
};

export function format_elite_level_name(level: string) {
	return LEVEL_NAMES[level] || level;
}

function pluralize(value: number, singular: string, plural: string) {
	return `${value} ${value === 1 ? singular : plural}`;
}

// Only the dimensions where the elite condition actually beats the standard one:
// some countries already allow more installments for everyone, and claiming an
// advantage there would be false.
function format_elite_advantage(downPayment: number | null, maxInstallments: number, benefit: EliteLevelBenefit) {
	const standardDownPayment = benefit.standard_down_payment_percent;
	const standardInstallments = benefit.standard_max_installments;
	const advantages: string[] = [];
	if (downPayment !== null && standardDownPayment !== null && standardDownPayment !== undefined) {
		// Round before comparing so a fractional edge never renders "0 puntos menos".
		const downPaymentDelta = Math.round(standardDownPayment - downPayment);
		if (downPaymentDelta > 0) advantages.push(`${pluralize(downPaymentDelta, 'punto', 'puntos')} menos de inicial`);
	}
	if (maxInstallments && standardInstallments !== null && standardInstallments !== undefined && maxInstallments > standardInstallments)
		advantages.push(`${pluralize(maxInstallments - standardInstallments, 'cuota más', 'cuotas más')}`);
	return advantages.length ? `${advantages.join(' · ')} que el resto de tiendas` : '';
}

function format_elite_benefit_detail(verb: string, benefit: EliteLevelBenefit) {
	const downPayment = benefit.down_payment_percent;
	const maxInstallments = benefit.additional_payment_options.reduce((max, option) => Math.max(max, option.installments), 0);
	let detail = '';
	if (downPayment !== null && maxInstallments) detail = `${verb} con ${downPayment}% de inicial y hasta ${maxInstallments} cuotas.`;
	else if (downPayment !== null) detail = `${verb} con ${downPayment}% de inicial.`;
	else if (maxInstallments) detail = `${verb} hasta en ${maxInstallments} cuotas.`;
	return { detail, advantage: detail ? format_elite_advantage(downPayment, maxInstallments, benefit) : '' };
}

export function format_elite_benefits(benefits: EliteBenefitsByLevel, benefitsActive = true) {
	const verb = benefitsActive ? 'Puedes vender' : 'Podrás vender';
	return Object.entries(benefits).flatMap(([level, products]) => {
		const levelName = format_elite_level_name(level);
		const productBenefits = Object.entries(products || {})
			.map(([product, benefit]) => ({ product, ...format_elite_benefit_detail(verb, benefit) }))
			.filter((entry) => entry.detail);
		if (!productBenefits.length) return [];
		// When several products share the same conditions, show them once for the whole level;
		// otherwise qualify each card by product so the popup never advertises terms the sale
		// flow won't honor for a given product. A single-product benefit is always qualified:
		// the backend only emits the products a level actually covers.
		const sharedAcrossProducts =
			productBenefits.length > 1 &&
			productBenefits.every(
				(entry) => entry.detail === productBenefits[0].detail && entry.advantage === productBenefits[0].advantage
			);
		if (sharedAcrossProducts)
			return [
				{
					key: level,
					level_key: level,
					level: levelName,
					detail: productBenefits[0].detail,
					advantage: productBenefits[0].advantage
				}
			];
		return productBenefits.map((entry) => ({
			key: `${level}:${entry.product}`,
			level_key: level,
			level: `${levelName} · ${PRODUCT_NAMES[entry.product] || entry.product}`,
			detail: entry.detail,
			advantage: entry.advantage
		}));
	});
}
